<template>
  <d-container
    fluid
    :class="{
      'main-content-container': true,
      'px-4': !agentID,
      'px-0': agentID
    }">
    <!-- Page Header -->
    <d-row
      v-if="!agentID"
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          {{ $t('customer.customers') }}
        </span>
        <h3 class="page-title">
          {{ $t('customer.customersList') }}
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          @click.prevent="$router.push('/klienci/nowy')">
          <i class="material-icons">add</i>
          {{ $t('customer.addNewCustomer') }}
        </d-button>

        <d-button
          @click.prevent="exportCustomers"
          theme="secondary"
          class="ml-2"
          :disabled="exportInProgress">
          <i
            v-if="!exportInProgress"
            class="material-icons">
            cloud_download
          </i>
          <div
            v-if="exportInProgress"
            class="spinner-border spinner-border-sm"
            role="status"></div>
          {{ $t('customer.exportCustomers') }}
        </d-button>
      </d-col>
    </d-row>

    <validation-observer
      ref="customers-filters-form"
      tag="form">
      <d-row class="d-flex flex-nowrap ml-0 mb-3 page-filters w-100">
        <d-input
          :placeholder="$t('customer.enterCustomerData')"
          v-model="filters.search"
          class="w-50" />

        <v-select
          class="ml-2 w-25"
          :options="countriesOptions"
          v-model="filters.country_id"
          :placeholder="$t('customer.customerCountryQuestionMark')"
          :searchable="false"
          label="text">
        </v-select>

        <select-agent
          v-if="!agentID"
          class="ml-2 w-25"
          :agentID="filters.agent_id" />

        <d-input
          :placeholder="$t('customer.enterCustomerAddress')"
          class="ml-2 w-25"
          v-model="filters.city" />
      </d-row>

      <d-row
        v-if="!agentID"
        class="d-flex flex-nowrap ml-0 mb-3 page-filters w-100">
        <d-form-select
          v-model="filters.discount_type"
          class="w-50"
          :options="[{
            text: $t('customer.selectDiscountType'),
            value: ''
          }, {
            text: $t('customer.selectDiscountTypes.discountProducts'),
            value: 'discount_products'
          }, {
            text: $t('customer.selectDiscountTypes.discountServices'),
            value: 'discount_services'
          }, {
            text: $t('customer.selectDiscountTypes.discountShipping'),
            value: 'discount_shipping'
          }]" />

        <div class="w-50 d-flex ml-2">
          <span class="pt-1">
            {{ $t('customer.discountRange') }}
          </span>
          <validation-provider
            :rules="'min_value:0|max_value:' + filters.discount_to"
            name="discount-from"
            class="ml-2"
            v-slot="{ errors }">
            <input
              type="number"
              :class="{
                'form-control': true,
                'is-invalid': errors.length
              }"
              :min="0"
              :max="100"
              :step="0.1"
              style="width:100px;"
              :disabled="filters.discount_type === ''"
              v-model="filters.discount_from" />
            <d-form-invalid-feedback>
              {{ errors[0] }}
            </d-form-invalid-feedback>
          </validation-provider>

          <span class="px-2 pt-1">&mdash;</span>

          <validation-provider
            :rules="'min_value:' + filters.discount_from + '|max_value:100'"
            name="discount-to"
            v-slot="{ errors }">
            <input
              type="number"
              :class="{
                'form-control': true,
                'is-invalid': errors.length
              }"
              :min="0"
              :max="100"
              :step="0.1"
              style="width:100px;"
              :disabled="filters.discount_type === ''"
              v-model="filters.discount_to" />
            <d-form-invalid-feedback>
              {{ errors[0] }}
            </d-form-invalid-feedback>
          </validation-provider>
        </div>
      </d-row>

      <d-row class="d-flex flex-nowrap ml-0 mb-3 page-filters w-100">
        <v-select
          class="w-50"
          :options="customerTags"
          v-model="filters.tags"
          :placeholder="$t('customer.customerTags')"
          :searchable="true"
          :multiple="true"
          label="name">
          <template slot="no-options">
            {{ $t('order.noTagsMatchingGivenPhrase') }}&hellip;
          </template>
        </v-select>

        <v-select
          style="width: 540px;"
          class="ml-2"
          :options="progressOrdersOptions"
          v-model="filters.progressOrders"
          :placeholder="$t('customer.isOrdersInRpogress')"
          :searchable="false"
          label="text">
        </v-select>

        <v-select
          style="width: 580px;"
          class="ml-2"
          :options="unpaidSettlementsOptions"
          v-model="filters.unpaidSettlements"
          :placeholder="$t('customer.isInvoicesNotSettled')"
          :searchable="false"
          label="text">
        </v-select>

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          @click.prevent="validateFiltersForm">
          {{ $t('ui.search') }}
        </d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">
          {{ $t('ui.resetFilters') }}
        </d-button>
      </d-row>
    </validation-observer>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">{{ $t('customer.companyName') }}</th>
            <th scope="col" class="border-0">{{ $t('settings.country.country') }}</th>
            <th scope="col" class="border-0" style="min-width: 200px;">{{ $t('customer.contact.contact') }}</th>
            <th scope="col" class="border-0" style="min-width: 200px;">{{ $t('customer.statistics') }}</th>
            <th scope="col" class="border-0">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(customer, index) of customers"
            :key="'customer-' + index">
            <td class="align-middle">
              {{ customer.name }}
              <br v-if="customer.is_supplier">
              <d-badge
                v-if="customer.is_supplier"
                theme="warning">
                {{ $t('ui.supplierAlt') }}
              </d-badge>
            </td>
            <td class="align-middle">
              {{ getCountry(customer.country_id) }}
            </td>
            <td class="align-middle">
              <small>
                {{ $t('customer.phoneShortCap') }}: {{ customer.phone }}<br>
                {{ $t('ui.eMailCap') }}: {{ customer.email }}<br>
                {{ $t('customer.newsletterAgreement') }}:
                <span
                  :class="{
                    'text-success': customer.newsletter_status === 'SUBSCRIBED',
                    'text-danger': customer.newsletter_status === 'UNSUBSCRIBED'
                  }">
                  <template v-if="customer.newsletter_status === 'SUBSCRIBED'">
                    {{ $t('customer.newsletterStatus.subscribed') }}
                  </template>
                  <template v-if="customer.newsletter_status === 'UNSUBSCRIBED'">
                    {{ $t('customer.newsletterStatus.unsubscribed') }}
                  </template>
                  <template v-if="customer.newsletter_status === null">
                    {{ $t('customer.newsletterStatus.null') }}
                  </template>
                </span>
              </small>
            </td>
            <td class="align-middle">
              <small>
                {{ $t('customer.ordersAndOffers') }}: {{ customer.orders_count }}<br>
                <template v-if="customer.progress_orders_count === 0">
                  {{ $t('customer.ordersInProgress') }}: {{ customer.progress_orders_count }}<br>
                </template>
                <strong v-else class="text-success">
                  {{ $t('customer.ordersInProgress') }}: {{ customer.progress_orders_count }}<br>
                </strong>
                <template v-if="customer.progress_offers_count === 0">
                  {{ $t('customer.offersInProgress') }}: {{ customer.progress_offers_count }}<br>
                </template>
                <strong v-else class="text-primary">
                  {{ $t('customer.offersInProgress') }}: {{ customer.progress_offers_count }}<br>
                </strong>
                <template v-if="customer.unpaid_settlements_count === 0">
                  {{ $t('customer.unsettledInvoices') }}: {{ customer.unpaid_settlements_count }}
                </template>
                <strong v-else class="text-danger">
                  {{ $t('customer.unsettledInvoices') }}: {{ customer.unpaid_settlements_count }}
                </strong>
              </small>
            </td>
            <td
              class="align-middle"
              style="width: 150px;">
              <d-button
                v-if="!agentID"
                theme="primary"
                @click.native="$router.push('/klienci/edycja/' + customer.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                v-if="!agentID && userIsAdmin"
                theme="danger"
                :disabled="removeInProgress"
                @click.native="removeCustomer(customer.id)"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>
              <d-button
                v-if="agentID"
                theme="info"
                @click.prevent.native="openCustomerInNewTab(customer.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">open_in_new</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="customers.length === 0">
            <td
              colspan="8"
              class="text-center">
              {{ $t('customer.noCustomersToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded"
        class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import CommonGetters from './../utils/CommonGetters.js';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';
import SelectAgent from './../components/common/SelectAgent.vue';
import Vue from 'vue';

export default {
  name: 'customers',
  mixins: [
    HasFilters,
    HasPagination
  ],
  props: {
    agentID: {
      type: Number,
      required: false,
      default: 0
    }
  },
  components: {
    'pagination': Pagination,
    'select-agent': SelectAgent
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedCountries;
    },
    disableFiltersInSubview () {
      return this.agentID;
    },
    progressOrdersOptions () {
      return [
        {
          text: this.$t('customer.withoutOrdersInProgress'),
          value: 0
        },
        {
          text: this.$t('customer.withOrdersInProgress'),
          value: 1
        }
      ];
    },
    unpaidSettlementsOptions () {
      return [
        {
          text: this.$t('customer.withoutUnsettledInvoices'),
          value: 0
        },
        {
          text: this.$t('customer.withUnsettledInvoices'),
          value: 1
        }
      ];
    },
    countriesOptions () {
      return this.countries.map(country => ({
        text: country.name,
        value: country.id
      }));
    },
    userIsAdmin () {
      return this.$store.getters.getUserType === 'admin';
    }
  },
  data() {
    return {
      countries: [],
      customers: [],
      customerTags: [],
      loaded: false,
      loadedCountries: false,
      loadedCustomerTags: false,
      loadError: false,
      removeInProgress: false,
      exportInProgress: false,
      filters: {
        search: '',
        country_id: null,
        agent_id: null,
        city: '',
        unpaidSettlements: '',
        progressOrders: '',
        discount_type: '',
        discount_from: 0,
        discount_to: 100,
        tags: null
      }
    };
  },
  mounted () {
    this.loadFilteredData();
    this.loadAdditionalData();
    this.$bus.$on('ui-select-agent-updated', this.setAgentID);
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {
        search: this.filters.search
      };

      if (this.filters.city) {
        where.city = this.filters.city;
      }

      if (this.agentID) {
        where.agent_id = this.agentID;
      } else if (this.filters.agent_id) {
        where.agent_id = this.filters.agent_id;
      }

      if (this.filters.tags !== null && this.filters.tags && this.filters.tags.length) {
        where.tags = this.filters.tags.map(tagData => tagData.id);
      }

      if (this.filters.progressOrders !== null && this.filters.progressOrders && typeof this.filters.progressOrders.value === 'number') {
        where.progress_orders = this.filters.progressOrders.value;
      }

      if (this.filters.unpaidSettlements !== null && this.filters.unpaidSettlements && typeof this.filters.unpaidSettlements.value === 'number') {
        where.unpaid_settlements = this.filters.unpaidSettlements.value;
      }

      if (this.filters.country_id) {
        where.country_id = this.filters.country_id.value;
      }

      if (this.filters.discount_type) {
        if (this.filters.discount_from) {
          where.discount_from = parseFloat(this.filters.discount_from);
        }

        if (this.filters.discount_to) {
          where.discount_to = parseFloat(this.filters.discount_to);
        }

        if (this.filters.discount_from || this.filters.discount_to) {
          where.discount_type = this.filters.discount_type;
        }
      }

      ListUtils.loadPaginatedItemsData(this, {
        method: 'get',
        endpoint: '/customers/items',
        listField: 'customers',
        params: {
          page: this.currentPage,
          where
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: '',
        country_id: null,
        agent_id: null,
        city: '',
        progressOrders: '',
        unpaidSettlements: '',
        discount_type: '',
        discount_from: 0,
        discount_to: 100,
        tags: null
      };

      this.$bus.$emit('ui-select-agent-reset');
      this.$bus.$emit('view-filters-reset');
      this.$bus.$emit('pagination-reset', false);
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['customers-filters-form'], this.debouncedLoadFilteredData);
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/countries/items',
        loadedKey: 'loadedCountries',
        outputKey: 'countries',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/customers/tags/items',
        loadedKey: 'loadedCustomerTags',
        outputKey: 'customerTags',
        errorKey: 'loadError',
        params: {
          return: 'get'
        }
      });
    },
    removeCustomer (customerID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/customers/delete/',
        id: customerID,
        successTitle: this.$t('customer.removeCustomerSuccessTitle'),
        successText: this.$t('customer.removeCustomerSuccessText'),
        confirmTitle: this.$t('customer.removeCustomerConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('customer.removeCustomerErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    getCountry (countryID) {
      return CommonGetters.getCountryById(countryID, this.countries);
    },
    exportCustomers () {
      this.exportInProgress = true;

      this.$http.post('/customers/export', {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', this.$t('customer.customerLowCase') + '.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.exportInProgress = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.exportErrorTitle'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.exportInProgress = false;
      });
    },
    setAgentID (agentData) {
      this.filters.agent_id = agentData.agentID;
    },
    openCustomerInNewTab (customerID) {
      window.open(this.$router.resolve('/klienci/edycja/' + customerID).href, '_blank');
    }
  },
  beforeDestroy () {
    this.$bus.$off('ui-select-agent-updated', this.setAgentID);
  }
};
</script>
